import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import CustomPagination from 'app/widgets/Shared/Pagination';
import Div from '@jumbo/shared/Div';
import storeServices from 'app/services/automations/store-services';
import StoreItem from './StoreItem';
import { CreateButton, SaveButton, StandardFiltersBar, StandardPageHeader, StandardSearchField } from 'app/shared/ui';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { COMPANY_MANAGER_PROFILES, hasAnyProfile } from 'app/utils/appHelpers';
import { Link as RouterLink } from 'react-router-dom';
import DataExportDialog from 'app/shared/downloads/DataExportDialog';
import { canEditShifts, canExportData } from 'app/shared/downloads/permissions';
import WorkShiftsEditor from 'app/shared/reports/WorkShiftsEditor';

const CREATE_STORE_ROLES = ['superadmin']
const DELETE_STORE_ROLES = ['superadmin']
const RENAME_STORE_ROLES = COMPANY_MANAGER_PROFILES
const DETACH_DEVICES_OPTION = '__DETACH_DEVICES__'

const StoresIndex = () => {
    const { authUser } = useJumboAuth();
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [recordsList, setRecordsList] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [listLoading, setListLoading] = useState(false)
    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [savingStore, setSavingStore] = useState(false)
    const [createError, setCreateError] = useState(null)
    const [createForm, setCreateForm] = useState({ name: '' })
    const [openRenameModal, setOpenRenameModal] = useState(false)
    const [savingRename, setSavingRename] = useState(false)
    const [renameError, setRenameError] = useState(null)
    const [renameForm, setRenameForm] = useState({ id: null, name: '', originalName: '' })
    const [storeToDelete, setStoreToDelete] = useState(null)
    const [deletingStore, setDeletingStore] = useState(false)
    const [deleteError, setDeleteError] = useState(null)
    const [devicesDestination, setDevicesDestination] = useState('')
    const [transferOptions, setTransferOptions] = useState([])
    const [loadingTransferOptions, setLoadingTransferOptions] = useState(false)
    const [storeToExport, setStoreToExport] = useState(null)
    const [exportNotice, setExportNotice] = useState(null)
    const [storeShifts, setStoreShifts] = useState(null)

    const canCreateStore = _.some(authUser?.current_profiles, (profile) => CREATE_STORE_ROLES.includes(profile?.name))
        || hasAnyProfile(authUser, CREATE_STORE_ROLES)
    const canRenameStore = _.some(authUser?.current_profiles, (profile) => RENAME_STORE_ROLES.includes(profile?.name))
        || hasAnyProfile(authUser, RENAME_STORE_ROLES)
    const canDeleteStore = _.some(authUser?.current_profiles, (profile) => DELETE_STORE_ROLES.includes(profile?.name))
        || hasAnyProfile(authUser, DELETE_STORE_ROLES)
    const devicesToMove = storeToDelete?.stats?.devices_qty || 0

    const buildRecordParams = useCallback(() => {
        let par = []
        
        par.push(`with_stats=true`)
        par.push(`page=${currentPage}`)
        if(searchQuery) {
            par.push(encodeURI(`q[name_or_kind_cont]=${searchQuery}`))
        }

        par = _.join(par, '&')

        return `?${par}`;
    }, [currentPage, searchQuery])

    const loadRecords = useCallback(() => {
        storeServices.getStores(buildRecordParams())
            .then(resp => {
                setRecordsList(resp.data)
                setTotalPages(resp.meta.total_pages)
                setTotalCount(resp.meta.total_count)
            })
            .catch(function (error) {})
            .finally(() => {
                setListLoading(false)
            });
    }, [buildRecordParams])

    useEffect(() => {
        if(listLoading){
            loadRecords()
        }
    }, [listLoading, loadRecords])

    useEffect(() => {
        setListLoading(true)
    }, [])

    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1)
        setSearchQuery(newVal)
        setListLoading(true)
    }

    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 350)

    const normalizeStoreResponse = (payload) => {
        if(payload?.data?.id && payload?.data?.attributes){
            return {
                id: payload.data.id,
                ...payload.data.attributes,
                stats: {
                    devices_qty: payload?.data?.attributes?.stats?.devices_qty || 0,
                    categories: payload?.data?.attributes?.stats?.categories || {},
                    silent_qty: payload?.data?.attributes?.stats?.silent_qty || 0,
                },
            }
        }
        const fallbackStore = payload?.store || payload || {}
        return {
            ...fallbackStore,
            stats: {
                devices_qty: fallbackStore?.stats?.devices_qty || 0,
                categories: fallbackStore?.stats?.categories || {},
                silent_qty: fallbackStore?.stats?.silent_qty || 0,
            },
        }
    }

    const openStoreCreateModal = () => {
        setCreateError(null)
        setCreateForm({ name: '' })
        setOpenCreateModal(true)
    }

    const closeStoreCreateModal = () => {
        if(savingStore) return
        setOpenCreateModal(false)
    }

    const createStore = () => {
        const trimmedName = `${createForm?.name || ''}`.trim()
        if(!trimmedName){
            setCreateError('El nombre del local es obligatorio.')
            return
        }

        setSavingStore(true)
        setCreateError(null)

        storeServices.createStore({
            store: {
                name: trimmedName,
            }
        })
            .then((resp) => {
                const newStore = normalizeStoreResponse(resp)
                if(newStore?.id){
                    setRecordsList((prev) => _.uniqBy([newStore, ...prev], 'id'))
                }
                setOpenCreateModal(false)
                setCurrentPage(1)
                setListLoading(true)
            })
            .catch((error) => {
                const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo crear el local.'
                setCreateError(msg)
            })
            .finally(() => {
                setSavingStore(false)
            })
    }

    const openStoreRenameModal = (store) => {
        setRenameError(null)
        setRenameForm({ id: store?.id, name: store?.name || '', originalName: store?.name || '' })
        setOpenRenameModal(true)
    }

    const closeStoreRenameModal = () => {
        if(savingRename) return
        setOpenRenameModal(false)
    }

    const renameStore = () => {
        const trimmedName = `${renameForm?.name || ''}`.trim()
        if(!trimmedName){
            setRenameError('El nombre del local es obligatorio.')
            return
        }

        setSavingRename(true)
        setRenameError(null)

        storeServices.updateStore(renameForm.id, {
            store: {
                name: trimmedName,
            }
        })
            .then(() => {
                setRecordsList((prev) => prev.map((r) => (
                    `${r.id}` === `${renameForm.id}` ? { ...r, name: trimmedName } : r
                )))
                setOpenRenameModal(false)
            })
            .catch((error) => {
                const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo renombrar el local.'
                setRenameError(msg)
            })
            .finally(() => {
                setSavingRename(false)
            })
    }

    const openStoreDeleteModal = (store) => {
        setDeleteError(null)
        setDevicesDestination('')
        setTransferOptions([])
        setStoreToDelete(store)

        if(!(store?.stats?.devices_qty > 0)) return

        // La lista de destinos no puede quedarse en la pagina actual: se piden todos los locales de la empresa.
        setLoadingTransferOptions(true)
        storeServices.getStores('?per_page=200')
            .then((resp) => {
                setTransferOptions(_.reject(resp?.data || [], (r) => `${r.id}` === `${store.id}`))
            })
            .catch(() => {
                setDeleteError('No se pudo cargar la lista de locales para el traspaso.')
            })
            .finally(() => {
                setLoadingTransferOptions(false)
            })
    }

    const closeStoreDeleteModal = () => {
        if(deletingStore) return
        setStoreToDelete(null)
    }

    const deleteStore = () => {
        if(!storeToDelete?.id) return

        let params = ''
        if(devicesToMove > 0){
            if(!devicesDestination){
                setDeleteError('Elige qué hacer con los dispositivos del local.')
                return
            }
            params = devicesDestination === DETACH_DEVICES_OPTION
                ? '?devices=detach'
                : `?transfer_to_store_id=${devicesDestination}`
        }

        setDeletingStore(true)
        setDeleteError(null)

        storeServices.deleteStore(storeToDelete.id, params)
            .then(() => {
                setRecordsList((prev) => _.reject(prev, (r) => `${r.id}` === `${storeToDelete.id}`))
                setStoreToDelete(null)
                setListLoading(true)
            })
            .catch((error) => {
                const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo eliminar el local.'
                setDeleteError(msg)
            })
            .finally(() => {
                setDeletingStore(false)
            })
    }

    const renderRecordsList = () => {
        if(!recordsList || recordsList.length === 0){
            return (
                <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="h6" mb={1}>No hay locales para mostrar</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchQuery ? 'Prueba con otra búsqueda.' : 'Los locales agrupan los dispositivos por recinto.'}
                    </Typography>
                </Paper>
            )
        }

        return (
            <Grid container spacing={2.5}>
                {_.map(recordsList, (r, i) => (
                    <Grid item xs={12} sm={6} lg={4} key={`store-${r.id || i}`}>
                        <StoreItem
                            store={r}
                            onEditClick={canRenameStore ? openStoreRenameModal : undefined}
                            onDeleteClick={canDeleteStore ? openStoreDeleteModal : undefined}
                            onExportClick={canExportData(authUser) ? setStoreToExport : undefined}
                            onShiftsClick={canEditShifts(authUser) ? setStoreShifts : undefined}
                        />
                    </Grid>
                ))}
            </Grid>
        )
    }

    return (
        <div>
            <StandardPageHeader
                title="Mis locales"
                subtitle={totalCount === 1 ? '1 local.' : `${totalCount || 0} locales.`}
                actions={canCreateStore ? (
                    <CreateButton onClick={openStoreCreateModal}>
                        Crear local
                    </CreateButton>
                ) : null}
            />

            <StandardFiltersBar>
                <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <StandardSearchField
                            fullWidth
                            placeholder="Buscar local"
                            onChange={(e) => debounceSearchQueryChange(e.target.value)}
                            onEnter={(value) => handleSearchQueryChange(value)}
                        />
                    </Grid>
                </Grid>
            </StandardFiltersBar>

            {!!exportNotice && (
                <Alert severity="success" onClose={() => setExportNotice(null)} sx={{ mb: 2 }}>
                    {exportNotice} <RouterLink to="/automations/downloads">Ver descargas</RouterLink>
                </Alert>
            )}

            <Div sx={{display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                <CircularProgress/>
            </Div>
            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {renderRecordsList()}
                </Grid>
            </Grid>
            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => {setCurrentPage(page); setListLoading(true)}}/>

            <DataExportDialog
                open={!!storeToExport}
                store={storeToExport}
                onClose={() => setStoreToExport(null)}
                onRequested={(file) => setExportNotice(`Estamos preparando «${file.title}». Te avisaremos por correo cuando esté lista.`)}
            />

            <Dialog fullWidth maxWidth="sm" open={!!storeShifts} onClose={() => setStoreShifts(null)}>
                <DialogTitle>Turnos de {storeShifts?.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Con estos turnos se arman los reportes por turno de este local.
                    </Typography>
                    {!!storeShifts && <WorkShiftsEditor storeId={storeShifts.id} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStoreShifts(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={openCreateModal}
                onClose={closeStoreCreateModal}
            >
                <DialogTitle>Crear local</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        {createError && (
                            <Grid item xs={12}>
                                <Alert severity="error">{createError}</Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoFocus
                                required
                                label="Nombre"
                                value={createForm.name}
                                onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={closeStoreCreateModal}
                        disabled={savingStore}
                    >
                        Cerrar
                    </Button>
                    <SaveButton
                        loading={savingStore}
                        disabled={!`${createForm?.name || ''}`.trim()}
                        onClick={createStore}
                    >
                        Guardar
                    </SaveButton>
                </DialogActions>
            </Dialog>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={openRenameModal}
                onClose={closeStoreRenameModal}
            >
                <DialogTitle>Renombrar local</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        {renameError && (
                            <Grid item xs={12}>
                                <Alert severity="error">{renameError}</Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                autoFocus
                                required
                                label="Nombre"
                                value={renameForm.name}
                                onChange={(e) => setRenameForm((prev) => ({ ...prev, name: e.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={closeStoreRenameModal}
                        disabled={savingRename}
                    >
                        Cerrar
                    </Button>
                    <SaveButton
                        loading={savingRename}
                        disabled={!`${renameForm?.name || ''}`.trim() || `${renameForm?.name || ''}`.trim() === renameForm?.originalName}
                        onClick={renameStore}
                    >
                        Guardar
                    </SaveButton>
                </DialogActions>
            </Dialog>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={Boolean(storeToDelete)}
                onClose={closeStoreDeleteModal}
            >
                <DialogTitle>Eliminar local</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 0.5 }}>
                        {deleteError && (
                            <Grid item xs={12}>
                                <Alert severity="error">{deleteError}</Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography variant="body1">
                                {devicesToMove > 0
                                    ? `"${storeToDelete?.name}" tiene ${devicesToMove} ${devicesToMove === 1 ? 'dispositivo' : 'dispositivos'}. Antes de eliminarlo hay que decidir dónde quedan.`
                                    : `Se va a eliminar "${storeToDelete?.name}".`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                Esta acción no se puede deshacer.
                            </Typography>
                        </Grid>
                        {devicesToMove > 0 && (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label="Qué pasa con los dispositivos"
                                    value={devicesDestination}
                                    disabled={loadingTransferOptions || deletingStore}
                                    helperText={loadingTransferOptions
                                        ? 'Cargando locales…'
                                        : 'Los dispositivos mantienen su zona, su historial y sus automatizaciones.'}
                                    onChange={(e) => setDevicesDestination(e.target.value)}
                                >
                                    {_.map(transferOptions, (option) => (
                                        <MenuItem key={`transfer-${option.id}`} value={`${option.id}`}>
                                            Traspasarlos a {option.name}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value={DETACH_DEVICES_OPTION}>Dejarlos sin local</MenuItem>
                                </TextField>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={closeStoreDeleteModal}
                        disabled={deletingStore}
                    >
                        Cerrar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={deletingStore || (devicesToMove > 0 && !devicesDestination)}
                        onClick={deleteStore}
                    >
                        {deletingStore ? 'Eliminando…' : 'Eliminar local'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default StoresIndex;
